import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { HERO, CATEGORY_TILE, heroStyles } from "@/data/products";
import { ProductCard } from "@/components/ProductCard";

const Eyebrow = ({ children, light }) => (
  <p className={`font-mono text-[11px] uppercase tracking-[0.24em] ${light ? "text-denim-accent" : "text-denim"}`}>{children}</p>
);

const Hero = () => (
  <section data-testid="hero" className="grid lg:grid-cols-2 min-h-[74vh]">
    <div className="order-2 lg:order-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-16 lg:py-0 bg-white">
      <div className="ar-rise max-w-md">
        <Eyebrow>The Digital Showroom</Eyebrow>
        <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold uppercase leading-[0.9] tracking-tight text-ink">
          American<br />Rag
        </h1>
        <p className="mt-6 text-muted-foreground text-base max-w-sm leading-relaxed">
          AR / American Rag — denim, graphic tees and fleece built on authentic American heritage since 1984. Explore the current line.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/new-originals" data-testid="hero-cta" className="group inline-flex items-center gap-2 bg-ink text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-navy transition-colors">
            View New Originals <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/women-denim" className="inline-flex items-center gap-2 border border-ink px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-ink hover:text-white transition-colors">
            Shop Denim
          </Link>
        </div>
      </div>
    </div>
    <div className="order-1 lg:order-2 relative min-h-[54vh] lg:min-h-0 bg-sand">
      <img src={HERO.home} alt="AR / American Rag denim" className="absolute inset-0 w-full h-full object-cover object-center" />
    </div>
  </section>
);

const DIVISIONS = [
  { title: "Men", to: "/men", img: CATEGORY_TILE["men-denim"], span: "md:col-span-8 md:row-span-2" },
  { title: "Women", to: "/women", img: CATEGORY_TILE["women-denim"], span: "md:col-span-4" },
  { title: "New Originals", to: "/new-originals", img: CATEGORY_TILE["men-tees"], span: "md:col-span-4" },
];

const Divisions = () => (
  <section data-testid="divisions" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
    <div className="mb-10 text-center">
      <Eyebrow>The Collections</Eyebrow>
      <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">Explore AR / American Rag</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] md:auto-rows-[260px] gap-4">
      {DIVISIONS.map((t) => (
        <Link key={t.title} to={t.to} data-testid={`division-${t.title.toLowerCase().replace(/ /g, "-")}`} className={`group relative overflow-hidden ${t.span}`}>
          <img src={t.img} alt={t.title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 text-white">
            <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight flex items-center gap-2">
              {t.title} <ArrowRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </h3>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const CATS = [
  ["Women's Denim", "/women-denim"], ["Men's Denim", "/men-denim"],
  ["Women's Graphic Tees", "/women-tees"], ["Men's Graphic Tees", "/men-tees"],
  ["Women's Fleece & Hoodies", "/women-fleece"], ["Men's Fleece & Hoodies", "/men-fleece"],
];

const ByCategory = () => (
  <section data-testid="by-category" className="bg-offwhite border-y border-border">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
      <div className="mb-10">
        <Eyebrow>Shop the Line</Eyebrow>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">Collections by Category</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CATS.map(([title, to]) => (
          <Link key={to} to={to} data-testid={`cat-link-${to.slice(1)}`} className="group relative overflow-hidden aspect-[4/5]">
            <img src={CATEGORY_TILE[to.slice(1)]} alt={title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 text-white">
              <h3 className="font-heading font-bold uppercase tracking-wide text-sm sm:text-base leading-tight">{title}</h3>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const SignatureCarousel = ({ products }) => {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <section data-testid="signature-carousel" className="py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <Eyebrow>Icons of the Line</Eyebrow>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">Signature Styles</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => scroll(-1)} aria-label="Previous" className="w-10 h-10 border border-border flex items-center justify-center hover:bg-ink hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /></button>
            <button onClick={() => scroll(1)} aria-label="Next" className="w-10 h-10 border border-border flex items-center justify-center hover:bg-ink hover:text-white transition-colors"><ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
      <div ref={ref} className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar px-4 sm:px-8 lg:px-12 snap-x snap-mandatory">
        {products.map((p, i) => (
          <div key={p.id} className="snap-start shrink-0 w-[60vw] sm:w-[40vw] md:w-[30vw] lg:w-[23%]">
            <ProductCard product={p} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
};

const AboutPreview = () => (
  <section data-testid="about-preview" className="bg-white">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2">
      <div className="order-2 lg:order-1 px-4 sm:px-8 lg:px-16 py-16 lg:py-28 flex flex-col justify-center">
        <Eyebrow>About the Brand</Eyebrow>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-ink">
          Defined by<br />no one.
        </h2>
        <p className="mt-5 text-muted-foreground text-base max-w-md leading-relaxed">
          AR / American Rag is a contemporary American fashion brand with a strong denim heritage — vintage-inspired graphics, authentic washes and premium embellishment, rooted in New York since 1984.
        </p>
        <div className="mt-8">
          <Link to="/about" data-testid="home-about-cta" className="inline-flex items-center gap-2 border border-ink px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-ink hover:text-white transition-colors">
            Read our story <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
      <div className="order-1 lg:order-2 relative min-h-[380px] lg:min-h-[600px] bg-sand">
        <img src={HERO.homeAlt} alt="AR / American Rag" className="absolute inset-0 w-full h-full object-cover object-top" />
      </div>
    </div>
  </section>
);

const WholesaleBand = () => (
  <section data-testid="wholesale-band" className="bg-navy text-white">
    <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20 text-center">
      <Eyebrow light>For Retailers & Buyers</Eyebrow>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">Partner with AR / American Rag</h2>
      <p className="mt-4 text-white/60 max-w-xl mx-auto text-sm">Bring our collections to your floor. We work with retailers, boutiques and wholesale buyers across North America.</p>
      <Link to="/wholesale" className="mt-8 inline-flex items-center gap-2 bg-white text-ink px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-denim hover:text-white transition-colors">
        Wholesale Inquiries <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

export default function Home() {
  const signature = heroStyles();
  return (
    <>
      <Hero />
      <Divisions />
      <SignatureCarousel products={signature} />
      <ByCategory />
      <AboutPreview />
      <WholesaleBand />
    </>
  );
}
